import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera, Loader2, Receipt, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MAX_RECEIPTS = 5;
const MAX_BYTES = 4 * 1024 * 1024;
const NONE = "__none__";

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const wrapReceipts = (arr) => (arr || []).map((src) => ({ id: makeId(), src }));

export default function CostDialog({ open, onOpenChange, issue, vendors = [], onSaved }) {
  const [partsCost, setPartsCost] = useState("");
  const [laborHours, setLaborHours] = useState("");
  const [laborRate, setLaborRate] = useState("");
  const [vendorId, setVendorId] = useState(NONE);
  const [receipts, setReceipts] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !issue) return;
    setPartsCost(issue.parts_cost != null ? String(issue.parts_cost) : "");
    setLaborHours(issue.labor_hours != null ? String(issue.labor_hours) : "");
    setLaborRate(issue.labor_rate != null ? String(issue.labor_rate) : "50");
    setVendorId(issue.vendor_id || NONE);
    setReceipts(wrapReceipts(issue.receipts));
  }, [open, issue]);

  const total =
    (Number(partsCost) || 0) + (Number(laborHours) || 0) * (Number(laborRate) || 0);

  const addFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_RECEIPTS - receipts.length;
    if (room <= 0) {
      toast.error(`Max ${MAX_RECEIPTS} receipts`);
      return;
    }
    files.slice(0, room).forEach((f) => {
      if (f.size > MAX_BYTES) return toast.error(`${f.name}: must be under 4MB`);
      const reader = new FileReader();
      reader.onload = () => setReceipts((prev) => [...prev, { id: makeId(), src: reader.result }]);
      reader.readAsDataURL(f);
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeReceipt = (id) => setReceipts((prev) => prev.filter((r) => r.id !== id));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        parts_cost: partsCost === "" ? null : Number(partsCost),
        labor_hours: laborHours === "" ? null : Number(laborHours),
        labor_rate: laborRate === "" ? null : Number(laborRate),
        vendor_id: vendorId === NONE ? null : vendorId,
        receipts: receipts.map((r) => r.src),
      };
      const { data } = await api.patch(`/issues/${issue.id}`, payload);
      toast.success("Costs saved");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cost-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">
            Cost & receipts
          </DialogTitle>
          <DialogDescription>
            Log parts, labor, and any vendor invoice for "{issue?.title}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Parts cost</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  data-testid="cost-parts"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={partsCost}
                  onChange={(e) => setPartsCost(e.target.value)}
                  className="pl-7"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger data-testid="cost-vendor">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— none —</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                      {v.category ? ` · ${v.category}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Labor hours</Label>
              <Input
                data-testid="cost-labor-hours"
                type="number"
                inputMode="decimal"
                step="0.25"
                min="0"
                value={laborHours}
                onChange={(e) => setLaborHours(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Hourly rate</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  data-testid="cost-labor-rate"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  value={laborRate}
                  onChange={(e) => setLaborRate(e.target.value)}
                  className="pl-7"
                  placeholder="50"
                />
              </div>
            </div>
          </div>

          {/* Total */}
          <div className="flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-4 py-3">
            <span className="text-xs uppercase tracking-[0.2em] text-gray-600">Total</span>
            <span className="font-display text-2xl font-bold tabular-nums" data-testid="cost-total">
              ${total.toFixed(2)}
            </span>
          </div>

          {/* Receipts */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">
              Receipts {receipts.length > 0 && `(${receipts.length}/${MAX_RECEIPTS})`}
            </Label>
            {receipts.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {receipts.map((r, idx) => (
                  <div key={r.id} className="relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                    <img src={r.src} alt={`receipt ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      data-testid={`remove-receipt-${idx}`}
                      onClick={() => removeReceipt(r.id)}
                      className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5"
                      aria-label="Remove receipt"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {receipts.length < MAX_RECEIPTS && (
              <button
                type="button"
                data-testid="add-receipt"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 hover:border-[#0055FF] rounded-md p-5 flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-[#0055FF] transition-colors"
              >
                <Receipt size={20} />
                <span className="text-sm">
                  {receipts.length === 0
                    ? "Tap to attach receipt photos"
                    : "Add another receipt"}
                </span>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              className="hidden"
              onChange={addFiles}
            />
            <p className="text-[11px] text-gray-500">
              <Camera size={10} className="inline -mt-0.5 mr-1" />
              Tip: snap a photo of paper invoices and they'll be saved here.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="cost-save"
            className="bg-[#0055FF] hover:bg-[#0044CC]"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : "Save costs"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
